// (session 10, P1/P2; owner 2026-10-08) what the pages show for a plan's price: the backend's CONFIGURED Stripe amounts
// (GET /billing/plans `price`), never a discount assumed in code. Pure (tested: test/plan-price.test.ts).
export interface PlanPrice { monthlyUsd: number; annualUsd: number | null }
export type Cycle = "monthly" | "annually";

export const usd = (n: number): string => (Number.isInteger(n) ? `$${n}` : `$${n.toFixed(2)}`);

/** The annual choice is offered only when every plan on the page has an annual price configured. */
export function annualOffered(prices: readonly (PlanPrice | null | undefined)[]): boolean {
  return prices.length > 0 && prices.every((p) => p != null && p.annualUsd != null);
}

/** A card's price: per month, and on the annual price what the year is billed. */
export function shownPrice(p: PlanPrice, cycle: Cycle): { perMonth: string; billedYearly: string | null } {
  if (cycle === "annually" && p.annualUsd != null)
    return { perMonth: usd(Math.round((p.annualUsd / 12) * 100) / 100), billedYearly: usd(p.annualUsd) };
  return { perMonth: usd(p.monthlyUsd), billedYearly: null };
}

/** The saving the annual price really gives, from the amounts ("Save 10%"; "Save up to 12%" when plans differ). */
export function annualSaving(prices: readonly PlanPrice[]): string | null {
  const pct = prices.filter((p) => p.annualUsd != null && p.monthlyUsd > 0)
    .map((p) => Math.round((1 - p.annualUsd! / (p.monthlyUsd * 12)) * 100));
  if (pct.length === 0 || Math.min(...pct) <= 0) return null;   // (audit) a plan whose annual price saves nothing: no badge at all
  return Math.min(...pct) === Math.max(...pct) ? `Save ${pct[0]}%` : `Save up to ${Math.max(...pct)}%`;
}

/** (session 10; owner 2026-10-08, consistent with D2) a plan's video length is shown only below long form: long-form videos
 *  (30 minutes) aren't offered, so the Agency card's "30 min" is not shown. null → no length line. */
export const LONG_FORM_SEC = 30 * 60;
export function shownMaxLength(seconds: number): string | null {
  if (seconds >= LONG_FORM_SEC) return null;
  return seconds >= 60 ? `${Math.floor(seconds / 60)} min` : `${seconds}s`;
}
